import { decode } from "html-entities";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import {
    getTotalChars,
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";
import sccLib from "@cclib/dict/608.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";

/**
 * Calculate metrics for an event (reusable across components)
 * @param {Object} event - The event object
 * @returns {Object} - Object containing plainText, lines, totalWords, totalChars, duration, wpm, cps
 */
export function getEventMetrics(event) {
    const plainText = convertToPlainText(event?.text || "");
    const lines = plainText.split("\n").map((line) => decode(line).length);
    const totalWords = plainText.split(/\s/gm).filter(w => w.length > 0).length;
    const totalChars = getTotalChars(plainText, "text");
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    const cpsChars = getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
    const duration = calculateDuration(event?.start, event?.end, 3);
    const wpm = calculateWpm(totalWords, duration);
    const cps = calculateCps(cpsChars, duration);

    return {
        plainText,
        lines,
        totalWords,
        totalChars,
        duration,
        wpm,
        cps
    };
}

/**
 * Check if an event has errors based on event group settings
 * @param {Object} event - The event object
 * @param {Object} eventGroup - The event group containing settings
 * @param {number} eventIndex - Index of the event in the group
 * @param {string} [errorTypeFilter="all"] - Filter by error type: "all", "timing", "content", "review"
 * @returns {boolean} - Whether the event has errors matching the filter
 */
export function hasEventErrors(event, eventGroup, eventIndex, errorTypeFilter = "all") {
    try {
        const errors = getEventErrors(event, eventGroup, eventIndex);
        
        if (errorTypeFilter === "all") {
            return errors.length > 0;
        }
        
        return errors.some(e => e.type === errorTypeFilter);
    } catch (err) {
        return false;
    }
}

/**
 * Get detailed errors for an event
 * @param {Object} event - The event object
 * @param {Object} eventGroup - The event group containing settings
 * @param {number} eventIndex - Index of the event in the group
 * @returns {Array} - Array of error objects with type and message
 */
export function getEventErrors(event, eventGroup, eventIndex) {
    const errors = [];
    
    if (!event || !eventGroup) return errors;

    const { plainText, lines, duration, wpm, cps } = getEventMetrics(event);

    // Timing errors
    if (wpm > eventGroup.maxWpm) {
        errors.push({ 
            type: "timing",
            msg: `WPM exceeds maximum set rate (${eventGroup.maxWpm}).`,
            hint: "Extend duration of Event, or reduce text content."
        });
    }
    
    if (cps > eventGroup.maxCps) {
        errors.push({ 
            type: "timing",
            msg: `CPS exceeds maximum set rate (${eventGroup.maxCps}).`,
            hint: "Extend duration of Event, or reduce text content."
        });
    }
    
    if (duration < eventGroup.minDuration || duration > eventGroup.maxDuration) {
        errors.push({ 
            type: "timing",
            msg: `Duration out of bounds. Event duration should be between (${eventGroup.minDuration} seconds - ${eventGroup.maxDuration} seconds).`,
            hint: "Increase/decrease the duration of the Event."
        });
    }

    // Check for overlap if enabled
    if (eventGroup.overlap) {
        if (event.start !== false && event.end !== false) {
            // Check overlap with previous event
            if (eventIndex > 0) {
                const prevEvent = eventGroup.events[eventIndex - 1];
                if (prevEvent && prevEvent.end !== false && event.start < prevEvent.end) {
                    errors.push({ 
                        type: "timing",
                        msg: `Event overlaps with previous event.`,
                        hint: "Adjust the start and end times to avoid overlap."
                    });
                }
            }
            // Check overlap with next event
            if (eventIndex < eventGroup.events.length - 1) {
                const nextEvent = eventGroup.events[eventIndex + 1];
                if (nextEvent && nextEvent.start !== false && event.end > nextEvent.start) {
                    errors.push({ 
                        type: "timing",
                        msg: `Event overlaps with next event.`,
                        hint: "Adjust the start and end times to avoid overlap."
                    });
                }
            }
        }
    }

    // Content errors
    if (lines.some((el) => el > eventGroup.maxChars) || lines.length > eventGroup.maxLines) {
        errors.push({ 
            type: "content",
            msg: `Text content exceeds maximums (Max Chars: ${eventGroup.maxChars}, Max Lines: ${eventGroup.maxLines}).`,
            hint: "Reduce the number of characters per line or reduce the number of lines in the Event."
        });
    }

    // Check for illegal characters if enabled
    if (eventGroup.illegalChars && plainText) {
        const illegalChar = plainText.split("").find((char) => {
            return sccLib.valid.indexOf(char) === -1;
        });

        if (illegalChar) {
            errors.push({ 
                type: "content",
                msg: `Plain text contains illegal 608 characters: ${illegalChar}`,
                hint: "Remove illegal characters from the text."
            });
        }
    }

    // Review errors
    if (event.approved === false) {
        errors.push({ 
            type: "review",
            msg: `Event marked as disapproved.`,
            hint: "Review the Event content and approve if correct."
        });
    }
    
    if (event.notes && !event.notesCheck) {
        errors.push({ 
            type: "review",
            msg: `Event has notes marked as incomplete.`,
            hint: "Review the notes and mark as complete if done."
        });
    }
    
    if (event.reply && !event.replyCheck) {
        errors.push({ 
            type: "review",
            msg: `Event has reply marked as incomplete.`,
            hint: "Review the reply and mark as complete if done."
        });
    }

    return errors;
}

/**
 * Check if an event has missing timecode
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event has missing start or end timecode
 */
export function hasMissingTimecode(event) {
    return event?.start === false || event?.end === false;
}

/**
 * Check if event is positioned at top of screen
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event is positioned at top
 */
export function isPositionedAtTop(event) {
    // yPos "start" means top of screen
    return event?.yPos === "start" || (typeof event?.yPos === "number" && event.yPos < 50);
}

/**
 * Check if event is positioned at bottom of screen
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event is positioned at bottom
 */
export function isPositionedAtBottom(event) {
    // yPos "end" means bottom of screen, "center" is middle
    return event?.yPos === "end" || (typeof event?.yPos === "number" && event.yPos >= 50);
}

/**
 * Check if event text contains search query (case-insensitive)
 * @param {Object} event - The event object
 * @param {string} query - Search query
 * @returns {boolean} - Whether the event text contains the query
 */
export function eventContainsText(event, query) {
    if (!query || query.trim() === "") return true;
    
    const plainText = convertToPlainText(event?.text || "").toLowerCase();
    const notes = (event?.notes || "").toLowerCase();
    const reply = (event?.reply || "").toLowerCase();
    const searchQuery = query.toLowerCase().trim();
    
    return plainText.includes(searchQuery) || 
           notes.includes(searchQuery) || 
           reply.includes(searchQuery);
}

/**
 * Check if event has any of the specified speakers
 * @param {Object} event - The event object
 * @param {Array} speakerIds - Array of speaker IDs to match
 * @returns {boolean} - Whether the event has any of the specified speakers
 */
export function eventHasSpeaker(event, speakerIds) {
    if (!speakerIds || speakerIds.length === 0) return true;
    if (!event?.speakers || event.speakers.length === 0) return false;
    
    return event.speakers.some(speaker => speakerIds.includes(speaker.id || speaker.name));
}

/**
 * Check if event has any of the specified tags
 * @param {Object} event - The event object
 * @param {Array} tagNames - Array of tag names to match
 * @returns {boolean} - Whether the event has any of the specified tags
 */
export function eventHasTag(event, tagNames) {
    if (!tagNames || tagNames.length === 0) return true;
    if (!event?.tags || event.tags.length === 0) return false;
    
    return event.tags.some(tag => {
        const tagValue = typeof tag === 'string' ? tag : (tag.term || tag.name);
        return tagNames.includes(tagValue);
    });
}

/**
 * Check if event is within timecode range
 * @param {Object} event - The event object
 * @param {number|null} startTime - Start time in seconds (null = no limit)
 * @param {number|null} endTime - End time in seconds (null = no limit)
 * @returns {boolean} - Whether the event falls within the timecode range
 */
export function eventInTimecodeRange(event, startTime, endTime) {
    if (event?.start === false || event?.end === false) return true;
    
    if (startTime !== null && event.start < startTime) return false;
    if (endTime !== null && event.end > endTime) return false;
    
    return true;
}

/**
 * Check if event matches caption style
 * @param {Object} event - The event object
 * @param {string} style - Style to match ("all", "Pop-On", "Roll-Up 2", etc.)
 * @returns {boolean} - Whether the event matches the style
 */
export function eventMatchesStyle(event, style) {
    if (!style || style === "all") return true;
    return event?.style === style;
}

/**
 * Check if event is forced
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event is forced
 */
export function eventIsForced(event) {
    return event?.forced === true;
}

/**
 * Check if event has failed approval (approved explicitly set to false)
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event has failed approval
 */
export function eventHasFailedApproval(event) {
    return event?.approved === false;
}

/**
 * Check if event has incomplete comment (note or reply with unchecked status)
 * @param {Object} event - The event object
 * @returns {boolean} - Whether the event has an incomplete comment
 */
export function eventHasIncompleteComment(event) {
    const hasUncheckedNote = event?.notes && !event?.notesCheck;
    const hasUncheckedReply = event?.reply && !event?.replyCheck;
    return hasUncheckedNote || hasUncheckedReply;
}

/**
 * Check if an event's text line count matches any of the selected counts.
 * A value of 4 (or greater) matches events with "4 or more" lines.
 * @param {Object} event - The event object
 * @param {Array<number>} lineCounts - Selected line counts to match
 * @returns {boolean} - True when no counts are selected or the event matches one
 */
export function eventMatchesLineCount(event, lineCounts) {
    if (!lineCounts || lineCounts.length === 0) return true;

    const { lines } = getEventMetrics(event);
    const count = lines.length;

    return lineCounts.some(lc => (lc >= 4 ? count >= 4 : count === lc));
}

/**
 * Check if an event matches any of the selected approval states (OR logic).
 * @param {Object} event - The event object
 * @param {Object} states - Selected approval states
 * @param {boolean} states.failed - Match approved === false
 * @param {boolean} states.approved - Match approved === true
 * @param {boolean} states.needsReview - Match approved === undefined
 * @returns {boolean} - True when no states are selected or the event matches one
 */
export function eventMatchesApproval(event, { failed, approved, needsReview }) {
    if (!failed && !approved && !needsReview) return true;
    if (failed && event?.approved === false) return true;
    if (approved && event?.approved === true) return true;
    if (needsReview && event?.approved === undefined) return true;
    return false;
}

/**
 * Check if an event matches any of the selected forced states (OR logic).
 * @param {Object} event - The event object
 * @param {Object} states - Selected forced states
 * @param {boolean} states.forced - Match forced === true
 * @param {boolean} states.nonForced - Match forced === false
 * @returns {boolean} - True when no states are selected or the event matches one
 */
export function eventMatchesForced(event, { forced, nonForced }) {
    if (!forced && !nonForced) return true;
    if (forced && event?.forced === true) return true;
    if (nonForced && event?.forced === false) return true;
    return false;
}

/**
 * Get indices of events that pass the current filter criteria
 * @param {Array} events - Array of event objects
 * @param {Object} eventGroup - The event group containing settings
 * @param {Object} filterState - The current filter state
 * @returns {Array<number>} - Array of indices for events that pass all filters
 */
export function getFilteredEventIndices(events, eventGroup, filterState) {
    if (!events || events.length === 0) return [];
    
    // Check if any filters are active
    const hasFilters = (
        filterState.text?.trim() !== "" ||
        filterState.hasError ||
        filterState.hasMissingTimecode ||
        filterState.hasForced ||
        filterState.hasNonForced ||
        filterState.hasFailedApproval ||
        filterState.hasApproved ||
        filterState.hasNeedsReview ||
        filterState.hasIncompleteComment ||
        (filterState.lineCounts && filterState.lineCounts.length > 0) ||
        (filterState.speakers && filterState.speakers.length > 0) ||
        (filterState.tags && filterState.tags.length > 0)
    );
    
    // If no filters are active, return all indices
    if (!hasFilters) {
        return events.map((_, index) => index);
    }
    
    // Apply filters and return matching indices
    return events
        .map((event, index) => ({ event, index }))
        .filter(({ event, index }) => {
            // Text search filter
            if (filterState.text && filterState.text.trim() !== "" && !eventContainsText(event, filterState.text)) {
                return false;
            }
            
            // Error filter
            if (filterState.hasError && !hasEventErrors(event, eventGroup, index)) {
                return false;
            }
            
            // Missing timecode filter
            if (filterState.hasMissingTimecode && !hasMissingTimecode(event)) {
                return false;
            }
            
            // Line count filter
            if (!eventMatchesLineCount(event, filterState.lineCounts)) {
                return false;
            }
            
            // Forced filter (forced / non-forced, OR within group)
            if (!eventMatchesForced(event, { forced: filterState.hasForced, nonForced: filterState.hasNonForced })) {
                return false;
            }
            
            // Approval filter (failed / approved / needs review, OR within group)
            if (!eventMatchesApproval(event, { failed: filterState.hasFailedApproval, approved: filterState.hasApproved, needsReview: filterState.hasNeedsReview })) {
                return false;
            }
            
            // Incomplete comment filter
            if (filterState.hasIncompleteComment && !eventHasIncompleteComment(event)) {
                return false;
            }
            
            // Speaker filter
            if (!eventHasSpeaker(event, filterState.speakers)) {
                return false;
            }
            
            // Tag filter
            if (!eventHasTag(event, filterState.tags)) {
                return false;
            }
            
            return true;
        })
        .map(({ index }) => index);
}
